import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { LeasingCalculatorData } from 'modules/leasingCalculator/components/common/LeasingCalculatorData';
import { LeasingCalculatorPathsEnum } from 'modules/leasingCalculator/routes';
import { parseFromCurrencyString } from 'modules/utils';
import {
  InterestRateType,
  LeasingOfferDetails,
  LeasingObjectDetails,
  LeasingType,
} from 'modules/types/LeasingOffer';
import {
  PaymentPeriodMap,
  DEFAULT_PAYMENTS_PER_YEAR,
} from 'modules/leasingCalculator/components/infoCalculation/components/common/helper';
import { PageWithHeader } from '../../../ui';
import { LeasingOfferOverviewLayout } from '../layout';
import { LeasingOffersPathsEnum } from '../../../leasingOffersPage/routes';

export interface SimpleLeasingOfferOverviewPageProps {}

const buildLeasingDetailsFromCalculator = (
  data: LeasingCalculatorData,
): LeasingOfferDetails => {
  const netPrice = +(data.leasingNetObjectPrice || 0);
  const ppmv = +(data.leasingObjectPpmv || 0);
  const totalPrice = netPrice + ppmv;
  const brutoTotalPrice = netPrice * 1.25 + ppmv;
  const isFinancial = data.leasingType?.name === LeasingType.FINANCIAL;
  const financingPeriodYears = +(data.financingPeriod?.id || 0);

  // Payment period
  const paymentPeriodName = data.leasingPaymentPeriodType?.name || 'Mjesečno';
  const paymentsPerYear =
    PaymentPeriodMap[paymentPeriodName] || DEFAULT_PAYMENTS_PER_YEAR;
  const numberOfPayments = financingPeriodYears * paymentsPerYear;

  const participationPct = isFinancial
    ? (data.financialLeasingParticipation || 0) / 100
    : (data.operationalLeasingParticipation || 0) / 100;

  const futureValuePct = isFinancial
    ? +(data.financialFutureValuePercentage || 0) / 100
    : +(data.operationalFutureValuePercentage || 0) / 100;

  // FL participation = pct * bruto
  // OL participation (neto) = pct * (netPrice + ppmv)
  const participationAmount = isFinancial
    ? brutoTotalPrice * participationPct
    : totalPrice * participationPct;

  const installment = isFinancial
    ? +parseFromCurrencyString(data.financialLeasingInstallment)
    : +parseFromCurrencyString(data.operationalLeasingInstallment);

  // Admin costs: FL and OL use different VAT treatment for PPMV
  const adminPctRaw = isFinancial
    ? +(data.financialAdminCostPercentage ?? data.adminCostPercentage ?? 0)
    : +(data.operationalAdminCostPercentage ?? data.adminCostPercentage ?? 0);
  const adminPct = adminPctRaw / 100;
  let adminTotal: number;
  let adminTaxes: number;
  let adminWithoutTaxes: number;

  if (isFinancial) {
    // FL: pct * brutoTotalPrice; PPMV excluded from VAT
    adminTotal = adminPct * brutoTotalPrice;
    adminTaxes = adminPct * netPrice * 0.25;
    adminWithoutTaxes = adminTotal - adminTaxes;
  } else {
    // OL: neto = pct * (netPrice + ppmv); entire neto subject to VAT
    adminWithoutTaxes = adminPct * totalPrice;
    adminTotal = adminWithoutTaxes * 1.25;
    adminTaxes = adminTotal - adminWithoutTaxes;
  }

  // Build leasingDetails array
  const leasingDetails: LeasingObjectDetails[] = [];
  let id = 1;

  // 1. Nabavna vrijednost objekta leasinga
  leasingDetails.push({
    id: id++,
    name: 'Nabavna vrijednost objekta leasinga',
    currency: 'EUR',
    amounts: {
      total: netPrice * 1.25,
      taxes: netPrice * 0.25,
      totalWithoutTaxes: netPrice,
    },
  });

  // 2. PPMV
  leasingDetails.push({
    id: id++,
    name: 'Posebni porez na motorna vozila (PPMV)',
    currency: 'EUR',
    amounts: {
      total: ppmv,
      taxes: 0,
      totalWithoutTaxes: ppmv,
    },
  });

  // 3. Nabavna vrijednost s PDV i PPMV
  leasingDetails.push({
    id: id++,
    name: 'Nabavna vrijednost objekta leasinga s PDV i PPMV',
    currency: 'EUR',
    amounts: {
      total: brutoTotalPrice,
      taxes: netPrice * 0.25,
      totalWithoutTaxes: totalPrice,
    },
  });

  // 4. Učešće / Akontacija
  if (isFinancial) {
    leasingDetails.push({
      id: id++,
      name: 'Učešće',
      currency: 'EUR',
      percentage: data.financialLeasingParticipation || 0,
      amounts: {
        total: participationAmount,
      },
    });
  } else {
    // OL: akontacija bruto = pct*netPrice*1.25 + pct*ppmv
    // OL: akontacija PDV = pct*netPrice*0.25 (PPMV excluded from VAT)
    // OL: akontacija neto = pct*(netPrice+ppmv)
    const opPartBruto =
      participationPct * netPrice * 1.25 + participationPct * ppmv;
    const opPartTaxes = participationPct * netPrice * 0.25;
    const opPartNeto = participationPct * totalPrice;
    leasingDetails.push({
      id: id++,
      name: 'Akontacija',
      currency: 'EUR',
      percentage: data.operationalLeasingParticipation || 0,
      amounts: {
        total: opPartBruto,
        taxes: opPartTaxes,
        totalWithoutTaxes: opPartNeto,
      },
    });
  }

  // 5. Iznos financiranja / Jamčevina
  if (isFinancial) {
    leasingDetails.push({
      id: id++,
      name: 'Iznos financiranja',
      currency: 'EUR',
      amounts: {
        total: brutoTotalPrice - participationAmount,
      },
    });
  } else {
    leasingDetails.push({
      id: id++,
      name: 'Jamčevina',
      currency: 'EUR',
      amounts: {
        total: 0,
        taxes: 0,
        totalWithoutTaxes: 0,
      },
    });
  }

  // 6. Otkupna vrijednost / Ostatak vrijednosti
  if (isFinancial) {
    // FL: otkupna = pct * brutoTotalPrice
    leasingDetails.push({
      id: id++,
      name: 'Otkupna vrijednost objekta leasinga',
      currency: 'EUR',
      percentage: +(data.financialFutureValuePercentage || 0),
      amounts: {
        total: brutoTotalPrice * futureValuePct,
      },
    });
  } else {
    // OL: ostatak bruto = pct*netPrice*1.25 + pct*ppmv
    // OL: ostatak PDV = pct*netPrice*0.25
    // OL: ostatak neto = pct*(netPrice+ppmv)
    const opRemBruto = futureValuePct * netPrice * 1.25 + futureValuePct * ppmv;
    const opRemTaxes = futureValuePct * netPrice * 0.25;
    const opRemNeto = futureValuePct * totalPrice;
    leasingDetails.push({
      id: id++,
      name: 'Ostatak vrijednosti',
      currency: 'EUR',
      percentage: +(data.operationalFutureValuePercentage || 0),
      amounts: {
        total: opRemBruto,
        taxes: opRemTaxes,
        totalWithoutTaxes: opRemNeto,
      },
    });
  }

  // 7. Iznos leasing rate / obrok
  if (isFinancial) {
    // FL: show bruto rata (the installment from calculator IS bruto)
    leasingDetails.push({
      id: id++,
      name: 'Iznos leasing rate',
      currency: 'EUR',
      amounts: {
        total: installment,
      },
    });
  } else {
    // OL: installment from calculator is neto obrok
    // Need to compute bruto with PPMV-aware VAT
    const ppmvPortion =
      (ppmv - ppmv * futureValuePct - ppmv * participationPct) /
      numberOfPayments;
    const obrokNeto = installment;
    const obrokBruto = (obrokNeto - ppmvPortion) * 1.25 + ppmvPortion;
    const obrokTaxes = obrokBruto - obrokNeto;
    leasingDetails.push({
      id: id++,
      name: 'Iznos leasing obroka',
      currency: 'EUR',
      amounts: {
        total: obrokBruto,
        taxes: obrokTaxes,
        totalWithoutTaxes: obrokNeto,
      },
    });
  }

  // 8. Administrativni troškovi
  leasingDetails.push({
    id: id++,
    name: 'Administrativni troškovi',
    currency: 'EUR',
    percentage: adminPct * 100,
    amounts: {
      total: adminTotal,
      taxes: adminTaxes,
      totalWithoutTaxes: adminWithoutTaxes,
    },
  });

  // 9. Iznos ukupne naknade na dan izdavanja ove ponude (OL only)
  // Sum of: naknada za sklapanje ugovora + akontacija + jamčevina + leasing obroci
  if (!isFinancial) {
    const opPartBruto =
      participationPct * netPrice * 1.25 + participationPct * ppmv;
    const ppmvPortion =
      (ppmv - ppmv * futureValuePct - ppmv * participationPct) /
      numberOfPayments;
    const obrokNeto = installment;
    const obrokBruto = (obrokNeto - ppmvPortion) * 1.25 + ppmvPortion;
    const jamcevina = 0;
    const ukupnaNaknada =
      adminTotal + opPartBruto + jamcevina + obrokBruto * numberOfPayments;
    leasingDetails.push({
      id: id++,
      name: 'Iznos ukupne naknade na dan izdavanja ove ponude',
      currency: 'EUR',
      amounts: {
        total: ukupnaNaknada,
      },
    });
  }

  // OL participation for payment details = akontacija bruto
  const participationForPayment = isFinancial
    ? participationAmount
    : participationPct * netPrice * 1.25 + participationPct * ppmv;

  return {
    id: 0,
    number: 0,
    numberText: '0',
    creationDate: new Date(),
    leasingTypeName: data.leasingType?.name,
    financingDuration: numberOfPayments,
    leasingObjectMake: '0',
    leasingObjectModel: '0',
    supplier: '0',
    interestRateType: InterestRateType.FIXED,
    interestRate: +(data.infoInterestRate || 0),
    paymentPeriod: paymentPeriodName,
    numberOfAnnuities: financingPeriodYears * 12,
    margin: 0,
    leasingDetails,
    participationAmount: participationForPayment,
    allowedUsageLevel: 0,
    effectiveInterestRate: 0,
  };
};

export const SimpleLeasingOfferOverviewPage: FC<
  SimpleLeasingOfferOverviewPageProps
> = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const calculatorFormData = useMemo<LeasingCalculatorData | undefined>(() => {
    const stored = localStorage.getItem('leasingCalculatorFormData');
    if (stored) {
      return JSON.parse(stored) as LeasingCalculatorData;
    }
    return undefined;
  }, []);

  const hasRequiredData = useMemo(() => {
    if (!calculatorFormData) return false;
    const d = calculatorFormData;
    return !!(
      d.leasingNetObjectPrice &&
      d.financingPeriod &&
      (d.infoInterestRate || d.infoInterestRate === 0) &&
      d.leasingType
    );
  }, [calculatorFormData]);

  useEffect(() => {
    if (!hasRequiredData) {
      history.replace(LeasingCalculatorPathsEnum.leasingCalculator);
    }
  }, [hasRequiredData, history]);

  const offerDetails = useMemo<LeasingOfferDetails | undefined>(() => {
    if (calculatorFormData && hasRequiredData) {
      return buildLeasingDetailsFromCalculator(calculatorFormData);
    }
    return undefined;
  }, [calculatorFormData, hasRequiredData]);

  const handleFormSubmit = useCallback(async () => {}, []);

  if (!hasRequiredData) {
    return null;
  }

  return (
    <PageWithHeader
      title={`${t('leasingOfferOverviewPage.breadcrumbs.offerOverview')}`}
    >
      <LeasingOfferOverviewLayout
        onSubmit={handleFormSubmit}
        onPrevious={() => history.goBack()}
        data={offerDetails}
        useLocalData
      />
    </PageWithHeader>
  );
};
